import { FormEvent, useContext, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import intl from "react-intl-universal";
import { FiArrowRight, FiCreditCard } from "react-icons/fi";
import { MyFirstAccessContext } from "..";
import { FirstAccessShell } from "../FirstAccessShell";
import {
  getFirstAccessErrorMessage,
  isFirstAccessFailure,
} from "../types";
import { FIRST_ACCESS_SKIP_LIVENESS } from "../utils";
import { cleanTaxIDMask, cpfMask } from "../../../../Utils/masks";
import { validateCPF_CNPJ } from "../../../../Utils/CPF_CNPJValidation";

export const ConfirmCpf = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { username, confirmCpf } = useContext(MyFirstAccessContext);

  const initialCpf = useMemo(() => {
    if (!username || username.includes("@")) return "";
    const digits = cleanTaxIDMask(username);
    return digits.length === 11 ? cpfMask(digits) : "";
  }, [username]);

  const [cpf, setCpf] = useState(initialCpf);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!username) {
    return <Redirect to="/auth/my-first-access" />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const digits = cleanTaxIDMask(cpf);
    if (digits.length !== 11 || validateCPF_CNPJ(digits) !== true) {
      setError(intl.get("firstAccess.cpfInvalid"));
      return;
    }

    setError("");
    setIsLoading(true);
    const result = await confirmCpf(username, digits);
    setIsLoading(false);

    if (isFirstAccessFailure(result)) {
      enqueueSnackbar(
        getFirstAccessErrorMessage(result, intl.get("firstAccess.cpfConfirmError")),
        { variant: "error" }
      );
      return;
    }

    const next =
      typeof result.data?.next === "string" ? result.data.next : undefined;
    const skipFace =
      FIRST_ACCESS_SKIP_LIVENESS ||
      next === "otp" ||
      result.data?.face_skipped === true;

    history.push(
      skipFace
        ? "/auth/my-first-access/validate-code"
        : "/auth/my-first-access/face"
    );
  };

  return (
    <FirstAccessShell
      title={intl.get("firstAccess.cpfTitle")}
      subtitle={intl.get("firstAccess.cpfSubtitle")}
    >
      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-field-group">
          <label className="login-field-label" htmlFor="first-access-cpf">
            {intl.get("firstAccess.cpfLabel")}
          </label>
          <div className="login-field">
            <span className="login-field__icon">
              <FiCreditCard />
            </span>
            <input
              id="first-access-cpf"
              name="cpf"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={intl.get("firstAccess.cpfPlaceholder")}
              value={cpf}
              onChange={(event) => {
                setError("");
                setCpf(cpfMask(cleanTaxIDMask(event.currentTarget.value)));
              }}
              className="login-field__input"
            />
          </div>
          {error ? <p className="login-field__error">{error}</p> : null}
        </div>

        <div className="login-verification-actions" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="login-verification-btn login-verification-btn--outline"
            onClick={() => history.push("/auth/my-first-access")}
            disabled={isLoading}
          >
            {intl.get("words.back")}
          </button>
          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading}
            style={{ flex: 1 }}
          >
            {isLoading ? (
              <>
                <span className="login-submit-btn__spinner" />
                {intl.get("firstAccess.continuing")}
              </>
            ) : (
              <>
                {intl.get("firstAccess.continue")}
                <FiArrowRight />
              </>
            )}
          </button>
        </div>
      </form>
    </FirstAccessShell>
  );
};
